import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical';

export type SerializedMentionNode = Spread<
  {
    mentionName: string;
    userId?: string;
    type: 'mention';
    version: 1;
  },
  SerializedTextNode
>;

function $convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent;
  const mentionName = domNode.getAttribute('data-lexical-mention-name');
  const userId = domNode.getAttribute('data-lexical-mention-user-id');

  if (textContent !== null) {
    const node = $createMentionNode(
      mentionName || textContent.replace(/^@/, ''),
      userId || undefined,
    );
    return { node };
  }

  return null;
}

const mentionStyle = `
  color: rgb(59, 130, 246);
  text-decoration: none;
  cursor: pointer;
`;

export class MentionNode extends TextNode {
  __mention: string;
  __userId?: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__userId, node.__key);
  }

  constructor(mentionName: string, userId?: string, key?: NodeKey) {
    // Remove @ if it exists and then add it back to ensure consistent format
    const cleanMentionName = mentionName.replace(/^@/, '');
    // Pass the text with @ to super
    super(`@${cleanMentionName}`, key);
    // Store the mention name without @
    this.__mention = cleanMentionName;
    this.__userId = userId;
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serializedNode.mentionName, serializedNode.userId);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionName: this.__mention,
      userId: this.__userId,
      type: 'mention',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = mentionStyle;
    dom.className = 'mention';
    dom.spellcheck = false;
    return dom;
  }

  // Override getText to ensure @ is always included
  getText(): string {
    return `@${this.__mention}`;
  }

  // Override getTextContent to ensure @ is always included
  getTextContent(): string {
    return `@${this.__mention}`;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', 'true');
    element.setAttribute('data-lexical-mention-name', this.__mention);
    if (this.__userId) {
      element.setAttribute('data-lexical-mention-user-id', this.__userId);
    }
    element.textContent = `@${this.__mention}`;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null;
        }
        return {
          conversion: $convertMentionElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createMentionNode(mentionName: string, userId?: string): MentionNode {
  const mentionNode = new MentionNode(mentionName, userId);
  mentionNode.setMode('token').toggleDirectionless();
  return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
